import React, { useState } from 'react';
import { Box, Typography, Button, Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon, BugReport, Dashboard as DashboardIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSwipeGesture } from '../hooks';
import AuthModal from './auth/AuthModal';
import BugReportModal from './BugReportModal';
import logoSymbol from '../assets/icons/logo-symbol.svg';
import judgeIcon from '../assets/icons/iconsax-judge.svg';
import documentIcon from '../assets/icons/iconsax-document-text-2.svg';
import fileSearchIcon from '../assets/icons/iconsax-file-search.svg';
import shieldSearchIcon from '../assets/icons/iconsax-shield-search.svg';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [headerAuthOpen, setHeaderAuthOpen] = useState(false);
  const [headerAuthMode, setHeaderAuthMode] = useState<'login' | 'register'>('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleProtectedRoute = (path: string) => {
    if (isAuthenticated) {
      navigate(path);
    } else {
      setHeaderAuthMode('login');
      setHeaderAuthOpen(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleMobileMenuItemClick = (path: string, isProtected = false) => {
    setMobileMenuOpen(false);
    if (isProtected) {
      handleProtectedRoute(path);
    } else {
      navigate(path);
    }
  };

  const menuItems = [
    { icon: judgeIcon, label: 'Právní konzultace', path: '/consultation-info', protected: false },
    { icon: documentIcon, label: 'Generování dokumentů', path: '/documents-info', protected: false },
    { icon: fileSearchIcon, label: 'Analýza smluv', path: '/contract-analysis-info', protected: false },
    { icon: shieldSearchIcon, label: 'Právní výzkum', path: '/research-info', protected: false },
    { icon: documentIcon, label: 'Blog', path: '/blog', protected: false },
  ];

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path);

  // Swipe gesture for closing mobile drawer
  const drawerRef = useSwipeGesture<HTMLDivElement>(
    {
      onSwipeRight: () => setMobileMenuOpen(false)
    },
    { threshold: 50 }
  );


  return (
    <Box sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: { xs: '64px', sm: '72px', md: '120px' },
      backgroundColor: '#F2F2F2',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: '1920px',
        height: '44px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        px: { xs: 2, sm: 3, md: 4, lg: 6 }
      }}>
        {/* Logo - Fixed to left */}
        <Box 
          onClick={() => navigate('/')}
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer',
            flexShrink: 0,
            position: 'absolute',
            left: { xs: 2, sm: 3, md: 4, lg: 6 },
            zIndex: 2
          }}
        >
          <Box
            component="img"
            src={logoSymbol}
            alt="Logo"
            sx={{
              width: { xs: '44px', sm: '50px', md: '60px' },
              height: { xs: '44px', sm: '50px', md: '60px' }
            }}
            loading="lazy"
          />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              '@media (min-width: 830px)': {
                display: 'flex'
              },
              '@media (max-width: 830px)': {
                display: 'none'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography sx={{
                fontFamily: '"Rajdhani", sans-serif !important',
                fontSize: '36px',
                fontWeight: 400,
                color: '#1F1F1F',
                letterSpacing: '-0.5px'
              }}>
                Pravni
              </Typography>
              <Typography sx={{
                fontFamily: '"Rajdhani", sans-serif !important',
                fontSize: '36px',
                fontWeight: 700,
                color: '#1F1F1F',
                letterSpacing: '-0.5px'
              }}>
                BOT
              </Typography>
              <Typography sx={{
                fontFamily: '"Rajdhani", sans-serif !important',
                fontSize: '36px',
                fontWeight: 400,
                color: '#1F1F1F',
                letterSpacing: '-0.5px'
              }}>
                .cz
              </Typography>
            </Box>
            <Typography
              component="a"
              href="https://apertia.cz"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: '13px',
                fontWeight: 400,
                color: '#666',
                letterSpacing: '0.3px',
                textAlign: 'center',
                marginTop: '-8px',
                textDecoration: 'none',
                cursor: 'pointer',
                transition: 'color 0.3s ease',
                '&:hover': {
                  color: '#e72f0d'
                }
              }}
            >
              Patří do skupiny Apertia
            </Typography>
          </Box>
        </Box>

        {/* Mobile Hamburger Menu */}
        {isMobile && (
          <Box sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center'
          }}>
            <IconButton
              aria-label="Otevřít hlavní menu"
              aria-controls="mobile-menu"
              aria-expanded={mobileMenuOpen ? 'true' : 'false'}
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                color: '#1F1F1F',
                padding: '12px',
                minWidth: '44px',
                minHeight: '44px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <MenuIcon sx={{ fontSize: { xs: '28px', sm: '32px' } }} />
            </IconButton>
          </Box>
        )}

        {/* Desktop Menu - Centered Menu Container */}
        {!isMobile && (
          <Box component="nav" id="primary-navigation" aria-label="Hlavní navigace" sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'fit-content',
            maxWidth: {
              md: 'calc(100% - 420px)',   // leave space for logo and right buttons
              lg: 'calc(100% - 560px)',
              xl: 'calc(100% - 720px)'
            }
          }}>
            <Box sx={{ 
              display: 'flex',
              gap: { xs: '4px', sm: '8px', md: '12px' },
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              {menuItems.map((item) => (
                <Box
                  key={item.path}
                  onClick={() => item.protected ? handleProtectedRoute(item.path) : navigate(item.path)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      item.protected ? handleProtectedRoute(item.path) : navigate(item.path);
                    }
                  }}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: { xs: '8px', sm: '10px 12px', md: '12px 16px', lg: '12px 18px' },
                    borderRadius: '12px',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    transition: 'all 0.4s ease-out',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <Box sx={{
                    width: { xs: '20px', sm: '18px' },
                    height: { xs: '20px', sm: '18px' },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Box 
                      component="img" 
                      src={item.icon} 
                      alt={item.label}
                      sx={{ 
                        width: '100%', 
                        height: '100%',
                        objectFit: 'contain'
                      }} 
                      loading="lazy"
                    />
                  </Box>
                  <Typography sx={{
                    fontFamily: 'Inter',
                    fontSize: { xs: '12px', sm: '13px', md: '14px' },
                    fontWeight: 400,
                    lineHeight: '20px',
                    color: '#1F1F1F',
                    display: { xs: 'none', lg: 'block' },
                  }}>
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* Auth Buttons - Fixed to right */}
        {isAuthenticated ? (
          <Box sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
            flexShrink: 0,
            position: 'absolute',
            right: { xs: 2, sm: 3, md: 4, lg: 6 },
            zIndex: 2
          }}>
            {/* Bug Report - icon on md/lg, full on xl */}
            <IconButton
              aria-label="Nahlásit problém"
              onClick={() => setBugReportOpen(true)}
              sx={{
                display: { xs: 'none', md: 'flex', xl: 'none' },
                color: '#FF9800',
                border: '1px solid #FF9800',
                width: 40,
                height: 40
              }}
            >
              <BugReport />
            </IconButton>
            <Button
              onClick={() => setBugReportOpen(true)}
              startIcon={<BugReport />}
              sx={{
                display: { xs: 'none', xl: 'flex' },
                padding: '8px 16px',
                backgroundColor: '#FF9800',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                border: 'none',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#F57C00',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                }
              }}
            >
              Nahlásit problém
            </Button>
            {/* Dashboard Button */}
            <Button
              onClick={() => navigate('/dashboard')}
              startIcon={<DashboardIcon sx={{ fontSize: 18 }} />}
              sx={{
                height: { xs: '36px', sm: '40px', md: '44px' },
                px: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: '12px',
                fontSize: { xs: '13px', sm: '14px' },
                fontWeight: 700,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                transition: 'all 0.2s ease',
                color: isActive('/dashboard') ? '#FFFFFF' : '#1F1F1F',
                background: isActive('/dashboard') 
                  ? '#1F1F1F' 
                  : 'linear-gradient(180deg, #FFFFFF 0%, #F7F7F7 100%)',
                border: isActive('/dashboard') ? '1px solid #1F1F1F' : '1px solid #E5E5E5',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
                  background: isActive('/dashboard')
                    ? '#1F1F1F'
                    : 'linear-gradient(180deg, #FFFFFF 0%, #EFEFEF 100%)'
                },
                '& .MuiButton-startIcon': {
                  marginRight: { xs: '6px', sm: '8px' }
                }
              }}
            >
              Dashboard
            </Button>
            {/* User Info */}
            <Typography sx={{
              fontFamily: 'Inter',
              fontSize: '14px',
              fontWeight: 500,
              color: '#1F1F1F',
              display: { xs: 'none', md: 'block' }
            }}>
              {user?.username}
            </Typography>
            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              sx={{
                height: { xs: '36px', sm: '40px', md: '44px' },
                padding: { xs: '8px 12px', sm: '10px 16px', md: '12px 20px' },
                bgcolor: '#1F1F1F !important',
                backgroundColor: '#1F1F1F !important',
                background: '#1F1F1F !important',
                color: '#FFFFFF !important',
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                border: 'none !important',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#e72f0d !important',
                  backgroundColor: '#e72f0d !important',
                  background: '#e72f0d !important'
                }
              }}
            >
              Odhlásit se
            </Button>
          </Box>
        ) : (
          <Box sx={{
            display: 'flex',
            gap: { xs: '8px', sm: '12px' },
            alignItems: 'center',
            flexShrink: 0,
            position: 'absolute',
            right: { xs: 2, sm: 3, md: 4, lg: 6 },
            zIndex: 2
          }}>
            {/* Bug Report - icon on md/lg, full on xl */}
            <IconButton
              aria-label="Nahlásit problém"
              onClick={() => setBugReportOpen(true)}
              sx={{
                display: { xs: 'none', md: 'flex', xl: 'none' },
                color: '#FF9800',
                border: '1px solid #FF9800',
                width: 40,
                height: 40
              }}
            >
              <BugReport />
            </IconButton>
            <Button
              onClick={() => setBugReportOpen(true)}
              startIcon={<BugReport />}
              sx={{
                display: { xs: 'none', xl: 'flex' },
                padding: '8px 16px',
                backgroundColor: '#FF9800',
                color: '#FFFFFF',
                fontSize: '13px',
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                border: 'none',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#F57C00',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)'
                }
              }}
            >
              Nahlásit problém
            </Button>
            {/* Login Button */}
            <Button
              onClick={() => { setHeaderAuthMode('login'); setHeaderAuthOpen(true); }}
              sx={{
                height: { xs: '36px', sm: '40px', md: '44px' },
                padding: { xs: '8px 12px', sm: '10px 16px', md: '12px 20px' },
                color: '#1F1F1F',
                border: '2px solid #1F1F1F !important',
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                boxSizing: 'border-box',
                '&:hover': {
                  backgroundColor: 'rgba(31, 31, 31, 0.08)',
                  border: '2px solid #1F1F1F !important'
                }
              }}
            >
              Přihlásit se
            </Button>
            {/* Register Button */}
            <Button
              onClick={() => { setHeaderAuthMode('register'); setHeaderAuthOpen(true); }}
              sx={{
                height: { xs: '36px', sm: '40px', md: '44px' },
                padding: { xs: '8px 12px', sm: '10px 16px', md: '12px 20px' },
                bgcolor: '#1F1F1F !important',
                backgroundColor: '#1F1F1F !important',
                background: '#1F1F1F !important',
                color: '#FFFFFF !important',
                fontSize: { xs: '12px', sm: '13px', md: '14px' },
                fontWeight: 600,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none !important',
                transition: 'all 0.3s ease',
                '&:hover': {
                  bgcolor: '#e72f0d !important',
                  backgroundColor: '#e72f0d !important',
                  background: '#e72f0d !important'
                }
              }}
            >
              <Box component="span" sx={{
                display: { xs: 'none', lg: 'inline' }
              }}>Zaregistrovat se</Box>
              <Box component="span" sx={{
                display: { xs: 'inline', lg: 'none' }
              }}>Přihlásit se</Box>
            </Button>
          </Box>
        )}

      </Box>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          id: 'mobile-menu',
          role: 'dialog',
          'aria-label': 'Hlavní menu' as any,
          'aria-labelledby': 'mobile-menu-title' as any,
          ref: drawerRef
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '85%', sm: '320px' },
            maxWidth: '400px',
            backgroundColor: '#F2F2F2',
            paddingTop: '20px'
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <Typography id="mobile-menu-title" sx={{
            fontFamily: '"Rajdhani", sans-serif !important',
            fontSize: '20px',
            fontWeight: 700,
            color: '#1F1F1F'
          }}>
            Menu
          </Typography>
          <IconButton
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Zavřít menu"
            sx={{
              color: '#1F1F1F',
              minWidth: '44px',
              minHeight: '44px',
              padding: '12px'
            }}
          >
            <CloseIcon sx={{ fontSize: '24px' }} />
          </IconButton>
        </Box>
        
        <List role="navigation" aria-label="Hlavní navigace" sx={{ paddingTop: '20px' }}>
          {menuItems.map((item) => (
            <ListItem 
              component="div"
              key={item.path}
              onClick={() => handleMobileMenuItemClick(item.path, item.protected)}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleMobileMenuItemClick(item.path, item.protected);
                }
              }}
              sx={{
                cursor: 'pointer',
                padding: '14px 20px',
                margin: '4px 12px',
                minHeight: '56px',
                borderRadius: '12px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}
              aria-current={isActive(item.path) ? 'page' : undefined}
            >
              <ListItemIcon sx={{ minWidth: '40px' }}>
                <Box 
                  component="img" 
                  src={item.icon} 
                  alt={item.label}
                  sx={{ 
                    width: '20px', 
                    height: '20px',
                    objectFit: 'contain'
                  }} 
                />
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontFamily: 'Inter',
                  fontSize: '16px',
                  fontWeight: 400,
                  color: '#1F1F1F'
                }}
              />
            </ListItem>
          ))}
          
          {/* Divider */}
          <Box sx={{
            height: '1px',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            margin: '20px 20px'
          }} />

          {/* Bug Report Button in Mobile Menu */}
          <ListItem
            component="div"
            onClick={() => {
              setMobileMenuOpen(false);
              setBugReportOpen(true);
            }}
            sx={{
              cursor: 'pointer',
              padding: '14px 20px',
              margin: '4px 12px',
              minHeight: '56px',
              borderRadius: '12px',
              bgcolor: '#FF9800',
              backgroundColor: '#FF9800',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: '#F57C00',
                backgroundColor: '#F57C00'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: '40px', color: '#FFFFFF' }}>
              <BugReport />
            </ListItemIcon>
            <ListItemText
              primary="Nahlásit problém"
              primaryTypographyProps={{
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 600,
                color: '#FFFFFF'
              }}
            />
          </ListItem>

          {/* Divider */}
          <Box sx={{
            height: '1px',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            margin: '20px 20px'
          }} />

          {/* Auth Buttons in Mobile Menu */}
          {isAuthenticated ? (
            <>
              <ListItem
                component="div"
                onClick={() => {
                  handleMobileMenuItemClick('/dashboard');
                }}
                sx={{
                  cursor: 'pointer',
                  padding: '14px 20px',
                  margin: '4px 12px',
                  minHeight: '56px',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <ListItemText 
                  primary="Dashboard"
                  primaryTypographyProps={{
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#1F1F1F'
                  }}
                />
              </ListItem>
              <ListItem
                component="div"
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                sx={{
                  cursor: 'pointer',
                  padding: '14px 20px',
                  margin: '4px 12px',
                  minHeight: '56px',
                  borderRadius: '12px',
                  bgcolor: '#1F1F1F',
                  backgroundColor: '#1F1F1F',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#e72f0d',
                    backgroundColor: '#e72f0d'
                  }
                }}
              >
                <ListItemText
                  primary="Odhlásit se"
                  primaryTypographyProps={{
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#FFFFFF'
                  }}
                />
              </ListItem>
            </>
          ) : (
            <>
              <ListItem
                component="div"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setHeaderAuthMode('login');
                  setHeaderAuthOpen(true);
                }}
                sx={{
                  cursor: 'pointer',
                  padding: '14px 20px',
                  margin: '4px 12px',
                  minHeight: '56px',
                  borderRadius: '12px',
                  border: '1px solid #1F1F1F',
                  '&:hover': {
                    backgroundColor: 'rgba(31, 31, 31, 0.04)'
                  }
                }}
              >
                <ListItemText 
                  primary="Přihlásit se"
                  primaryTypographyProps={{
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#1F1F1F'
                  }}
                />
              </ListItem>
              <ListItem
                component="div"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setHeaderAuthMode('register');
                  setHeaderAuthOpen(true);
                }}
                sx={{
                  cursor: 'pointer',
                  padding: '14px 20px',
                  margin: '4px 12px',
                  minHeight: '56px',
                  borderRadius: '12px',
                  bgcolor: '#1F1F1F',
                  backgroundColor: '#1F1F1F',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: '#e72f0d',
                    backgroundColor: '#e72f0d'
                  }
                }}
              >
                <ListItemText
                  primary="Zaregistrovat se"
                  primaryTypographyProps={{
                    fontFamily: 'Inter',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#FFFFFF'
                  }}
                />
              </ListItem>
            </>
          )}
        </List>
      </Drawer>

      {/* Auth Modal */}
      <AuthModal
        open={headerAuthOpen}
        onClose={() => setHeaderAuthOpen(false)}
        initialMode={headerAuthMode}
      />

      {/* Bug Report Modal */}
      <BugReportModal
        open={bugReportOpen}
        onClose={() => setBugReportOpen(false)}
      />
    </Box>
  );
};

export default Header;
